// The organisation's audit log, read (spec/13 §15, spec/07 §11.2; F-ORG-14 #71, #394): the rows over
// `GET /v1/organizations/{o}/audit`, newest first and cursor-paginated, with the filter bar (or the
// trace banner), a row's detail in a centred dialog, and the CSV export over `POST …/audit/export`.
//
// One component serves two screens: the organisation area's Audit log, and a player's Audit tab. The
// tab passes a `fixedTarget` (`targetType=player&targetId=…`) that is merged into every read and
// export and is never shown as a removable filter, so the tab is the same log narrowed to one player.
//
// The rows arrive already masked (spec/07 §11): personal data in `before`/`after` is rendered as the
// server sent it, never unmasked.

import type { AuditEntry, AuditEntryList } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, EmptyState, VisuallyHidden } from '@clavian/ui-web';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useCallback, useId, useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { fieldErrors, PROBLEM, problemCode } from '../../api/problems';
import { queryKeys } from '../../api/queries';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice, TableSkeleton } from '../../ui';
import { saveFile } from './save-file';
import { traceIdOf, traceUrlOf } from './trace';
import { useAuditTimes } from './time';
import { FilterBar, type AuditView } from './filter-bar';
import {
  auditQuery,
  EXPORT_MAX_ROWS,
  fallbackFileName,
  fileNameOf,
  readerTimeZone,
  withRange,
  type AuditFilter,
  type AuditRange,
} from './filters';
import { useAuditLabels, useDirectory, type Directory } from './labels';
import { RowDetail } from './row-detail';
import { AuditTable } from './rows';

import './audit.css';

export interface FixedTarget {
  readonly targetType: string;
  readonly targetId: string;
}

const WHOLE_LOG: AuditView = { filter: {}, range: 'all' };

export interface AuditLogProps {
  readonly organizationId: string;
  /** A player's tab fixes the target; the organisation screen leaves it out. */
  readonly fixedTarget?: FixedTarget;
}

export function AuditLog({ organizationId, fixedTarget }: AuditLogProps): ReactNode {
  const labels = useAuditLabels();
  const directory = useDirectory();
  const times = useAuditTimes();
  const [view, setView] = useState<AuditView>(WHOLE_LOG);
  const [beforeTrace, setBeforeTrace] = useState<AuditView>(WHOLE_LOG);
  const exporter = useExport(organizationId, view.filter, fixedTarget);

  // The reference zone for the quick ranges: a filtered venue's, or the reader's own.
  const zoneOf = useCallback(
    (filter: AuditFilter): string => (filter.venueId === undefined ? readerTimeZone() : (directory.timezone(filter.venueId) ?? readerTimeZone())),
    [directory],
  );

  const show = useCallback(
    (filter: AuditFilter, range: AuditRange) => {
      exporter.reset();
      // A calendar range is recomputed in the (possibly new) reference zone so it stays right as the venue changes.
      const next = range === 'custom' || range === 'all' ? filter : withRange(filter, range, zoneOf(filter));
      setView({ filter: next, range });
    },
    [exporter, zoneOf],
  );

  const trace = useCallback(
    (requestId: string) => {
      if (view.filter.requestId === undefined) setBeforeTrace(view);
      exporter.reset();
      setView({ filter: { requestId }, range: 'all' });
    },
    [view, exporter],
  );

  const traced = view.filter.requestId;

  return (
    <div className="console-audit">
      <ExportBar exporter={exporter} />
      {traced === undefined ? (
        <FilterBar
          view={view}
          labels={labels}
          directory={directory}
          times={times}
          zoneLabel={zoneLabelOf(view.filter, directory)}
          hideTarget={fixedTarget !== undefined}
          onFilter={(filter) => show(filter, filter.from === undefined && filter.to === undefined ? 'all' : view.range)}
          onRange={(range: AuditRange) => show(view.filter, range)}
          onApply={(filter) => {
            const sameWindow = filter.from === view.filter.from && filter.to === view.filter.to;
            const windowed = filter.from !== undefined || filter.to !== undefined;
            setView({ filter, range: !windowed ? 'all' : sameWindow ? view.range : 'custom' });
            exporter.reset();
          }}
          onTrace={trace}
        />
      ) : (
        <TraceBanner requestId={traced} onWholeLog={() => show(beforeTrace.filter, beforeTrace.range)} />
      )}
      <Rows
        organizationId={organizationId}
        view={view}
        fixedTarget={fixedTarget}
        labels={labels}
        directory={directory}
        times={times}
        onTrace={trace}
        onClear={() => show({}, 'all')}
      />
    </div>
  );
}

function zoneLabelOf(filter: AuditFilter, directory: Directory): string {
  if (filter.venueId !== undefined) {
    const zone = directory.timezone(filter.venueId);
    if (zone !== undefined) return `${directory.venue(filter.venueId) ?? filter.venueId} · ${zone}`;
  }
  return readerTimeZone();
}

// ---------------------------------------------------------------------------------------------
// Trace
// ---------------------------------------------------------------------------------------------

function TraceBanner({ requestId, onWholeLog }: { readonly requestId: string; readonly onWholeLog: () => void }) {
  const { t } = useTranslation('console');
  const traceId = traceIdOf(requestId);
  const url = traceUrlOf(requestId);
  const titleId = useId();
  return (
    <section className="console-audit-trace" aria-labelledby={titleId}>
      <span className="console-audit-trace__icon" aria-hidden="true">
        <Icon name="info" />
      </span>
      <div className="console-audit-trace__body">
        <h2 id={titleId} className="console-audit-trace__title">
          {t('audit.trace.heading')}
        </h2>
        <p className="console-audit-trace__note">{t('audit.trace.banner')}</p>
        <dl className="console-audit-trace__ids">
          <div>
            <dt>{t('audit.trace.requestId')}</dt>
            <dd>
              <code>{requestId}</code>
            </dd>
          </div>
          {traceId === undefined ? null : (
            <div>
              <dt>{t('audit.trace.traceId')}</dt>
              <dd>
                <code>{traceId}</code>
              </dd>
            </div>
          )}
        </dl>
        {url === undefined ? null : (
          <a href={url} target="_blank" rel="noreferrer">
            {t('audit.trace.open')}
          </a>
        )}
      </div>
      <div className="console-audit-trace__action">
        <Button size="sm" onClick={onWholeLog}>
          {t('audit.trace.clear')}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// The rows
// ---------------------------------------------------------------------------------------------

function Rows({
  organizationId,
  view,
  fixedTarget,
  labels,
  directory,
  times,
  onTrace,
  onClear,
}: {
  readonly organizationId: string;
  readonly view: AuditView;
  readonly fixedTarget: FixedTarget | undefined;
  readonly labels: ReturnType<typeof useAuditLabels>;
  readonly directory: Directory;
  readonly times: ReturnType<typeof useAuditTimes>;
  readonly onTrace: (requestId: string) => void;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('console');
  const api = useApi();
  const [open, setOpen] = useState<AuditEntry | null>(null);
  // The fixed target rides every read and export, so the tab is one player's rows and nothing else.
  const query = useMemo(() => ({ ...view.filter, ...(fixedTarget ?? {}) }) as AuditFilter, [view.filter, fixedTarget]);

  const log = useInfiniteQuery({
    queryKey: queryKeys.organizationAudit(organizationId, query),
    queryFn: ({ pageParam }): Promise<AuditEntryList> =>
      api.listOrganizationAudit({ path: { organizationId }, query: { ...auditQuery(query), ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    refetchOnWindowFocus: false,
  });

  const traced = view.filter.requestId;
  const filtered = Object.keys(view.filter).length > 0;
  const rows = log.data?.pages.flatMap((page) => page.data) ?? [];
  const onOpen = useCallback((row: AuditEntry) => setOpen(row), []);

  return (
    <section className="console-audit-results" aria-labelledby="console-audit-rows-title" aria-busy={log.isFetching || undefined}>
      <VisuallyHidden>
        <h2 id="console-audit-rows-title">{t('audit.rows.title')}</h2>
      </VisuallyHidden>
      {log.data === undefined ? (
        log.isError ? (
          <ReadFailure error={log.error} onRetry={() => void log.refetch()} />
        ) : (
          <TableSkeleton rows={8} label={t('audit.rows.loading')} />
        )
      ) : (
        <>
          {rows.length === 0 ? null : (
            <p className="console-audit-count">{t(log.hasNextPage ? 'audit.rows.shownMore' : 'audit.rows.shown', { count: rows.length })}</p>
          )}
          <AuditTable
            rows={rows}
            caption={traced === undefined ? t('audit.rows.caption') : t('audit.rows.captionTrace', { id: traced })}
            empty={
              traced !== undefined ? (
                <EmptyState size="compact" icon={<NavIcon name="audit" />} title={t('audit.rows.emptyTrace')} />
              ) : filtered ? (
                <EmptyState
                  size="compact"
                  icon={<NavIcon name="audit" />}
                  title={t('audit.rows.empty')}
                  description={t('audit.rows.emptyDescription')}
                  action={<Button onClick={onClear}>{t('audit.filters.clear')}</Button>}
                />
              ) : fixedTarget !== undefined ? (
                <EmptyState size="compact" icon={<NavIcon name="audit" />} title={t('audit.rows.emptyPlayer')} />
              ) : (
                <EmptyState size="compact" icon={<NavIcon name="audit" />} title={t('audit.rows.emptyLog')} />
              )
            }
            labels={labels}
            directory={directory}
            times={times}
            onOpen={onOpen}
            now={log.dataUpdatedAt}
          />
          {log.hasNextPage ? (
            <div className="console-audit-more-rows">
              <Button busy={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
                {t('audit.rows.more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
      <RowDetail
        row={open}
        labels={labels}
        directory={directory}
        times={times}
        now={log.dataUpdatedAt}
        onClose={() => setOpen(null)}
        onTrace={(requestId) => {
          setOpen(null);
          onTrace(requestId);
        }}
      />
    </section>
  );
}

function ReadFailure({ error, onRetry }: { readonly error: unknown; readonly onRetry: () => void }) {
  const { t } = useTranslation('console');
  const refused = fieldErrors(error);
  if (problemCode(error) === PROBLEM.validationFailed && refused.length > 0) {
    return (
      <Notice tone="danger" role="alert" title={t('audit.rows.refused')}>
        <ul className="console-audit-refused">
          {refused.map((problem) => (
            <li key={`${problem.field} ${problem.message}`}>
              <code>{problem.field}</code>: {problem.message}
            </li>
          ))}
        </ul>
      </Notice>
    );
  }
  return <LoadFailure error={error} message={t('audit.rows.failed')} onRetry={onRetry} />;
}

// ---------------------------------------------------------------------------------------------
// The export
// ---------------------------------------------------------------------------------------------

function useExport(organizationId: string, filter: AuditFilter, fixedTarget: FixedTarget | undefined) {
  const api = useApi();
  return useMutation({
    mutationFn: async () => {
      let name: string | undefined;
      const csv = await api.exportOrganizationAudit({
        path: { organizationId },
        body: auditQuery({ ...filter, ...(fixedTarget ?? {}) }),
        headers: { accept: 'text/csv, application/problem+json' },
        onResponse: (head) => {
          name = fileNameOf(head.headers.get('content-disposition'));
        },
      });
      return { csv, name: name ?? fallbackFileName(organizationId) };
    },
    onSuccess: ({ csv, name }) => saveFile(name, csv, 'text/csv;charset=utf-8'),
  });
}

function ExportBar({ exporter }: { readonly exporter: ReturnType<typeof useExport> }) {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return (
    <div className="console-audit-export">
      <Button busy={exporter.isPending} onClick={() => exporter.mutate()}>
        {t('audit.export.submit')}
      </Button>
      <p role="status" className={exporter.isSuccess ? 'console-audit-export__done' : 'console-audit-export__idle'}>
        {exporter.isSuccess ? t('audit.export.done', { file: exporter.data.name }) : ''}
      </p>
      {exporter.isError ? (
        problemCode(exporter.error) === PROBLEM.unprocessable ? (
          <Notice tone="warn" role="alert">
            {t('audit.export.tooMany', { max: format.number(EXPORT_MAX_ROWS) })}
          </Notice>
        ) : (
          <LoadFailure error={exporter.error} message={t('audit.export.failed')} />
        )
      ) : null}
    </div>
  );
}
